// PURPOSE: Show unresolved outreach copy fields and stored cadence previews.
// CONTRACT: Sender binds at drafting time; all other unresolved markers require attention.
// DEPENDS: Native record cadence_emails data.
// CEILING: Presentation only; pushing remains allowed and nothing sends mail.
import type { NativeRecord } from './types';

type Copy = { subject?: string; body?: string; drafted_on?: string; sent_on?: string };
function copies(record: NativeRecord): [string, Copy][] {
  const value = record.data.cadence_emails;
  return value && typeof value === 'object' && !Array.isArray(value)
    ? Object.entries(value).flatMap(([rung, item]) => item && typeof item === 'object' && !Array.isArray(item)
      ? [[rung, Object.fromEntries(['subject', 'body', 'drafted_on', 'sent_on'].map(key => [key, typeof item[key] === 'string' ? item[key] : '']))] as [string, Copy]] : []) : [];
}

function copyDate(value?: string) {
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isNaN(date.getTime()) ? 'Date unknown' : new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'America/Toronto' }).format(date);
}

export function CopyNeeds({ record }: { record: NativeRecord }) {
  const fields = [...new Set(copies(record).flatMap(([, copy]) =>
    [...`${copy.subject || ''} ${copy.body || ''}`.matchAll(/\[\[([^\[\]]+)\]\]/g)].map(match => match[1])
  ))].filter(name => name !== 'sender_first_name');
  return fields.length ? <p className="small muted">Needs: {fields.join(', ')}</p> : null;
}

export function OutreachCopy({ record }: { record: NativeRecord }) {
  const emails = copies(record);
  return emails.length ? <section aria-label="Drafted emails"><h3>Drafted emails</h3><CopyNeeds record={record} />
    {emails.map(([rung, copy]) => <details key={rung}><summary>{({ first_touch: 'Email 1', mark1: 'Email 2', mark2: 'Email 3' } as Record<string, string>)[rung] || rung}</summary><p className="small muted">Drafted {copyDate(copy.drafted_on || (record.data.last_drafted_rung === rung ? String(record.data.last_drafted_on || '') : ''))} · {copy.sent_on ? `Sent ${copyDate(copy.sent_on)}` : 'Not sent yet'}</p><strong>{copy.subject}</strong><p style={{ whiteSpace: 'pre-wrap' }}>{copy.body}</p></details>)}
  </section> : null;
}
